import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import PageHeader, { DetailRow } from "../components/PageHeader";
import CtaSection from "../components/CtaSection";
import BookingLink from "../components/BookingLink";
import { Reveal, RevealGroup, RevealItem } from "../components/Reveal";
import { BOOKING_SERVICES, SERVICE_TOPICS } from "../lib/site";

export default function Services() {
  return (
    <>
      <Seo
        title="Full-Cycle Accounting Services for Business | Directive Optive"
        description="Full cycle bookkeeping and accounting services: transactions, reconciliations, payables and receivables, payroll support, financial reports, and planning."
        path="/services"
      />
      <PageHeader
        eyebrow="Services"
        title="Specific services, not vague promises."
        lead="Every service below starts with a conversation about who it’s for, what problem it solves, and exactly what we’ll do together. Scope and deliverables are confirmed before work begins."
        image="/img/photo-team-laughing.webp"
        imageAlt="Three Directive Optive Solutions colleagues laughing together around a laptop"
      />

      <section className="py-24 lg:py-32" data-testid="services-detail-section">
        <div className="container-shell">
          <Reveal className="max-w-3xl">
            <h2 className="font-serif text-3xl text-olive sm:text-4xl">One team for the whole accounting cycle.</h2>
            <p className="mt-5 text-base leading-relaxed text-moss sm:text-lg">From account setup to financial reports, we cover the full cycle. So your books, your payables, your payroll records, and your planning all live with the same team that knows your business.</p>
          </Reveal>
          <div className="mt-14 border-b border-line">
            {SERVICE_TOPICS.map((t, i) => (
              <DetailRow key={t.title} title={t.title} testId={`services-topic-row-${i + 1}`}>
                <p><span className="font-bold text-ink">Who it’s for:</span> {t.for}</p>
                <p className="mt-2"><span className="font-bold text-ink">Problem it addresses:</span> {t.solves}</p>
                <p className="mt-2"><span className="font-bold text-ink">May include:</span> {t.includes}</p>
              </DetailRow>
            ))}
          </div>
          <Reveal className="mt-12 max-w-3xl rounded-md border border-olive/25 bg-sage p-8">
            <h3 className="font-serif text-xl text-olive">What to prepare before we start</h3>
            <p className="mt-3 text-base leading-relaxed text-moss">Whatever you have: recent bank and credit card statements, prior bookkeeping or tax records, and a rough sense of what’s been keeping you stuck. Missing pieces are fine. Organizing them is part of the work. We’ll never ask you to send tax documents or account numbers through this website.</p>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-sage/85 py-24 lg:py-32" data-testid="services-booking-section">
        <div className="container-shell relative z-10">
          <Reveal className="max-w-3xl">
            <p className="eyebrow">Book directly</p>
            <h2 className="mt-5 font-serif text-3xl text-olive sm:text-4xl">Reserve a time online.</h2>
            <p className="mt-5 text-base leading-relaxed text-moss sm:text-lg">Pick a slot in our booking calendar. Consultations run Monday to Friday, 9 AM to 5 PM Mountain Time — you’ll get an instant confirmation.</p>
          </Reveal>
          <RevealGroup className="mt-12 grid gap-6 md:grid-cols-3">
            {BOOKING_SERVICES.map((s) => {
              const slug = s.url.split("/booking-calendar/")[1];
              return (
                <RevealItem key={s.name} className="h-full">
                  <Link to={`/booking-calendar/${slug}`} data-testid={`services-booking-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex h-full flex-col rounded-md border border-line bg-white p-8 transition-colors duration-300 hover:bg-sage">
                    <h3 className="flex-1 font-serif text-2xl text-olive">{s.name}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-moss">{s.blurb}</p>
                    <span className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-pine">
                      Book now <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </Link>
                </RevealItem>
              );
            })}
          </RevealGroup>
          <Reveal delay={0.15} className="mt-12">
            <BookingLink page="services-page" dataTestId="services-page-book-button" className="btn-primary">
              Book a Consultation <ArrowUpRight className="h-4 w-4" />
            </BookingLink>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden py-20 lg:py-24" data-testid="services-tax-section">
        <img src="/img/photo-meeting.jpg" alt="" aria-hidden width="1200" height="800" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/55" />
        <div className="container-shell relative z-10">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Planning conversations</p>
            <h2 className="mt-5 font-serif text-3xl text-olive sm:text-4xl">Tax planning, on both sides of the ledger.</h2>
            <p className="mt-6 text-base leading-relaxed text-moss sm:text-lg">Beyond the books, we talk planning: business tax planning and personal tax planning are separate, dedicated conversations. And they work best together.</p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link to="/tax-planning/business" data-testid="services-link-business-tax" className="btn-outline">Business Tax Planning</Link>
              <Link to="/tax-planning/personal" data-testid="services-link-personal-tax" className="btn-outline">Personal Tax Planning</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
